import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  useGetTenantQuery,
  useUpdateTenantThemeInfoMutation,
} from 'api/tenant.api';
import tenantStyles from 'assets/jss/material-dashboard-pro-react/views/tenantStyle.js';
import Button from 'components/CustomButtons/Button.js';
import ColorPicker from 'components/Pickers/ColorPicker.js';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  blackColor,
  defaultFont,
  infoColor,
  primaryColor,
  secondaryColor,
  whiteColor,
} from '@src/themes/defaultThemeProps';
import { PromptAlert } from '../../features/ux/RouterPrompt';
import ThemePreview from '../../views/Tenant/ThemePreview';
import { AntSwitch } from '../CustomInput/AntSwitch';

const useTenantStyles = makeStyles(tenantStyles);

export default function ThemeTab({ onSuccessTab, tab, setDirtyTabStatus }) {
  const classes = useTenantStyles();
  const { data: tenant } = useGetTenantQuery();
  const [updateTheme] = useUpdateTenantThemeInfoMutation();

  const [theme, setTheme] = useState(tenant.theme ? { ...tenant.theme } : {});
  const [sectionThemeIsDirty, setSectionThemeIsDirty] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);

  useEffect(() => {
    setTheme(tenant.theme ? { ...tenant.theme } : {});
  }, [tenant]);

  useEffect(() => {
    if (setDirtyTabStatus) {
      setDirtyTabStatus(sectionThemeIsDirty);
    }
  }, [sectionThemeIsDirty]);

  const themeChangeHandler = (updatedThemeProps) => {
    const updatedTheme = { ...theme, ...updatedThemeProps };
    setTheme(updatedTheme);
    setSectionThemeIsDirty(true);
  };

  const revertForm = () => {
    setTheme({
      fontFamily: tenant.theme?.fontFamily ?? defaultFont.fontFamily,
      textColor: tenant.theme?.textColor ?? blackColor,
      backgroundColor: tenant.theme?.backgroundColor ?? whiteColor,
      primaryColor: tenant.theme?.primaryColor ?? primaryColor[0],
      primaryContrastTextColor:
        tenant.theme?.primaryContrastTextColor ?? primaryColor[-1],
      secondaryColor: tenant.theme?.secondaryColor ?? secondaryColor[0],
      secondaryContrastTextColor:
        tenant.theme?.secondaryContrastTextColor ?? secondaryColor[-1],
      tertiaryColor: tenant.theme?.tertiaryColor ?? infoColor[0],
      tertiaryContrastTextColor:
        tenant.theme?.tertiaryContrastTextColor ?? infoColor[-1],
      headerVisible: tenant.theme?.headerVisible ?? false,
      // Removed the receiptLogo fallback as it is no longer relevant.
    });
    setSectionThemeIsDirty(false);
  };

  const saveChanges = async () => {
    try {
      await updateTheme({
        tenantThemeInfo: {
          ...theme,
          tenantId: tenant._id,
        },
      }).unwrap();

      setSectionThemeIsDirty(false);

      toast.success('Changes saved successfully', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });

      if (onSuccessTab) {
        onSuccessTab(tab.name);
      }
    } catch (err) {
      toast.error('An error occurred while ' + 'trying to save the changes', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const hasErrors = useCallback(() => {
    const requiredFields = [
      theme.fontFamily,
      theme.textColor,
      theme.backgroundColor,
      theme.primaryColor,
      theme.primaryContrastTextColor,
      theme.secondaryColor,
      theme.secondaryContrastTextColor,
      theme.tertiaryColor,
      theme.tertiaryContrastTextColor,
    ];

    return requiredFields.some((field) => !field || field === '');
  }, [theme]);

  return (
    <>
      {showWarningAlert && (
        <PromptAlert
          message='You have unsaved changes. Are you sure you want to discard them?'
          title='Discard Changes'
          onConfirm={() => {
            revertForm();
            setShowWarningAlert(false);
          }}
          onCancel={() => setShowWarningAlert(false)}
        />
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <h4>Font and Colors</h4>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl variant='standard' className={classes.formControlInline}>
            <InputLabel
              id='font-family-select-label'
              className={classes.inputLabel}
            >
              Font Family
            </InputLabel>
            <Select
              variant='standard'
              className={classes.select}
              labelId='font-family-select-label'
              id='font-family-select'
              value={theme.fontFamily ?? 'Helvetica'}
              onChange={(event) =>
                themeChangeHandler({ fontFamily: event.target.value })
              }
            >
              <MenuItem value='Arial'>Arial</MenuItem>
              <MenuItem value='Andale Mono'>Andalé Mono</MenuItem>
              <MenuItem value='Arial Black'>Arial Black</MenuItem>
              <MenuItem value='Baskerville'>Baskerville</MenuItem>
              <MenuItem value='Bradley Hand'>Bradley Hand</MenuItem>
              <MenuItem value='Brush Script MT'>Brush Script MT</MenuItem>
              <MenuItem value='Comic Sans MS'>Comic Sans MS</MenuItem>
              <MenuItem value='Courier'>Courier</MenuItem>
              <MenuItem value='Georgia'>Georgia</MenuItem>
              <MenuItem value='Gill Sans'>Gill Sans</MenuItem>
              <MenuItem value='Helvetica'>Helvetica</MenuItem>
              <MenuItem value='Impact'>Impact</MenuItem>
              <MenuItem value='Lucida'>Lucida</MenuItem>
              <MenuItem value='Luminari'>Luminari</MenuItem>
              <MenuItem value='Monaco'>Monaco</MenuItem>
              <MenuItem value='Palatino'>Palatino</MenuItem>
              <MenuItem value='Tahoma'>Tahoma</MenuItem>
              <MenuItem value='Times New Roman'>Times New Roman</MenuItem>
              <MenuItem value='Trebuchet MS'>Trebuchet MS</MenuItem>
              <MenuItem value='Verdana'>Verdana</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl variant='standard' className={classes.formControlInline}>
            <FormLabel
              className={classes.inputLabel}
              htmlFor='header-visible-switch'
            >
              Header visible
            </FormLabel>
            <AntSwitch
              id='header-visible-switch'
              labelId='header-visible-switch-label'
              checked={theme.headerVisible ?? false}
              onChange={(e) =>
                themeChangeHandler({ headerVisible: e.target.checked })
              }
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Base Text: use on base background'
            selectedColor={theme.textColor}
            onChange={(color) => themeChangeHandler({ textColor: color })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Base Background: use on screens'
            selectedColor={theme.backgroundColor}
            onChange={(color) => themeChangeHandler({ backgroundColor: color })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Primary Background: use on header and primary buttons'
            selectedColor={theme.primaryColor}
            onChange={(color) => themeChangeHandler({ primaryColor: color })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Primary Text: use on primary background'
            selectedColor={theme.primaryContrastTextColor}
            onChange={(color) =>
              themeChangeHandler({ primaryContrastTextColor: color })
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Secondary Background: use on secondary buttons'
            selectedColor={theme.secondaryColor}
            onChange={(color) => themeChangeHandler({ secondaryColor: color })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Secondary Text: use on secondary background'
            selectedColor={theme.secondaryContrastTextColor}
            onChange={(color) =>
              themeChangeHandler({ secondaryContrastTextColor: color })
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Tertiary Background: use on tertiary buttons'
            selectedColor={theme.tertiaryColor}
            onChange={(color) => themeChangeHandler({ tertiaryColor: color })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            label='Tertiary Text: use on tertiary background'
            selectedColor={theme.tertiaryContrastTextColor}
            onChange={(color) =>
              themeChangeHandler({ tertiaryContrastTextColor: color })
            }
          />
        </Grid>

        <Grid item xs={12}>
          <h4>Theme preview: Font and Colors</h4>
          <Box sx={{ marginLeft: 2 }}>
            <ThemePreview theme={theme} />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box className={classes.buttonContainer}>
            <Button
              color='primary'
              onClick={saveChanges}
              disabled={!sectionThemeIsDirty || !tab.active || hasErrors()}
              className={classes.registerButton}
            >
              Save Changes
            </Button>
            <Button
              color='secondary'
              onClick={() => setShowWarningAlert(true)}
              disabled={!sectionThemeIsDirty}
              className={classes.registerButton}
            >
              Cancel
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
